import type { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import type { SolarDesignDraftState } from '@payaca/helpers/solarDesignDraft';
import { initialiseSolarGeneration } from '@payaca/helpers/solarGenerationAvailability';
import {
  recordRoofBearingRules,
  type SolarPlacementRules,
} from '@payaca/helpers/solarPlacementRules';
import {
  solarAnalysedLayoutStartsFromRepack,
  solarEnergyForFaceFrom,
  solarPackAllFaces,
  solarPlaceAnalysedFace,
} from '@payaca/helpers/solarPackLayout';
import { hasMeasuredPlane } from '@payaca/helpers/solarPlaneFit';
import { type SolarSavingsRegion } from '@payaca/helpers/solarSavingsConstants';
import { panelCentreOnProperty } from '@/ui/pages/solarDesignerPage/solarDesigner/parcelClip';

/** An empty hand-drawn design: no analysis, the designer opens ready to draw. */
export const newManualSolarDesignState = (
  placementRules: SolarPlacementRules
): SolarDesignDraftState => ({
  electrical: { mode: 'wholeDesign' },
  selectedPanelApplianceId: null,
  inverter: { mode: 'automatic' },
  selectedBatteryApplianceId: null,
  includeBattery: false,
  batteryQuantity: 1,
  configIndex: null,
  selectedPanelIndexes: [],
  layout: {
    workingFaces: [],
    workingPanels: [],
    workingObstacles: [],
    faceOffsets: {},
    faceLinks: [],
    excludedFaceIds: [],
    manualMode: true,
    geometryEdited: false,
  },
  placementRules,
  roofBearingRuleByFace: {},
  monthlyPolicy: null,
});

/**
 * The same initial working set used when opening a fresh analysis in Glow:
 * the analysed faces, packed exactly as "Repack all roofs" would pack them
 * with the account's rules. The analysis' own panels are kept only for a
 * YourPower layout or when whole-roof Repack would refuse (a face without a
 * physical plane).
 */
export const newSolarDesignState = (
  source: ProjectSolarPotential,
  options: {
    placementRules: SolarPlacementRules;
    region?: SolarSavingsRegion;
  }
): SolarDesignDraftState => {
  if (source.roofRecoveryReason)
    throw new Error(
      'This analysis requires a hand-drawn roof before creating a design.'
    );
  const rules = options.placementRules;
  const state = analysedSolarDesignState(source, rules);
  if (
    !source.geometry ||
    !solarAnalysedLayoutStartsFromRepack(source.geometry) ||
    state.layout.workingFaces.length === 0
  )
    return state;
  const geometry = source.geometry;
  // Mirrors SolarDesignerContent's analysed module and the layout hook's defaults.
  const widthMeters =
    geometry.panelDimensions?.widthMeters ?? source.panelWidthMeters ?? 1.045;
  const heightMeters =
    geometry.panelDimensions?.heightMeters ?? source.panelHeightMeters ?? 1.879;
  const parcelRing = geometry.parcel?.ring ?? null;
  const packed = solarPackAllFaces({
    faces: state.layout.workingFaces,
    offsets: {},
    context: {
      obstacles: state.layout.workingObstacles,
      panelDims: { widthMeters, heightMeters },
      rules,
      region: options.region ?? 'uk',
      energyForFace: solarEnergyForFaceFrom(geometry.packedPanels),
      includedByParcel: (corners) => panelCentreOnProperty(corners, parcelRing),
      generationDimensions: {
        widthMeters,
        heightMeters,
        capacityWatts:
          geometry.panelDimensions?.capacityWatts ??
          source.panelCapacityWatts ??
          400,
      },
    },
    respectRoofSkips: true,
  });
  if (!packed) return state;
  return {
    ...state,
    roofBearingRuleByFace: recordRoofBearingRules(
      packed.faces.map((face) => face.id),
      rules,
      {},
      true
    ),
    layout: {
      ...state.layout,
      workingFaces: packed.faces,
      workingPanels: packed.panels,
    },
  };
};

/** The analysis copied as-is: its faces, obstacles and its own panel placement. */
const analysedSolarDesignState = (
  source: ProjectSolarPotential,
  placementRules: SolarPlacementRules
): SolarDesignDraftState => {
  return {
    electrical: { mode: 'wholeDesign' },
    selectedPanelApplianceId: null,
    inverter: { mode: 'automatic' },
    selectedBatteryApplianceId: null,
    includeBattery: false,
    batteryQuantity: 1,
    configIndex: source.configurations[source.defaultConfigurationIndex]
      ? source.defaultConfigurationIndex
      : null,
    selectedPanelIndexes: source.panels
      .slice(
        0,
        source.configurations[source.defaultConfigurationIndex]?.panelsCount ??
          source.panels.length
      )
      .map((_, i) => i),
    layout: {
      workingFaces: (source.geometry?.faces ?? []).map(
        (face, id): SolarDesignDraftState['layout']['workingFaces'][number] =>
          solarPlaceAnalysedFace(
            {
              ...face,
              roofPlane:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? {
                      pitchDegrees: face.roofPitchDegrees,
                      azimuthDegrees: face.roofAzimuthDegrees,
                    }
                  : undefined,
              mounting:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? { mode: 'auto' }
                  : undefined,
              arrayAzimuthDegrees: hasMeasuredPlane(face)
                ? face.azimuthDegrees
                : undefined,
              ...(face.roofPitchDegrees != null &&
              face.roofAzimuthDegrees != null
                ? {
                    appliedMounting: face.panelsOnTiltFrames
                      ? ('tilt' as const)
                      : ('flush' as const),
                  }
                : {}),
              generation: initialiseSolarGeneration(
                face.generation,
                face,
                (source.geometry?.packedPanels ?? []).filter(
                  (panel) => panel.faceIndex === id
                ),
                source.geometry?.panelDimensions ?? {
                  widthMeters: 1.045,
                  heightMeters: 1.879,
                  capacityWatts: 400,
                }
              ),
              id,
              isNew: false,
              pitchSource:
                face.roofPitchDegrees != null && face.roofAzimuthDegrees != null
                  ? ('dsm' as const)
                  : ('assumed' as const),
              groupId: null,
            },
            {
              edited: !!source.geometry?.edited,
              rules: placementRules,
              generationDimensions: source.geometry?.panelDimensions ?? {
                widthMeters: 1.045,
                heightMeters: 1.879,
                capacityWatts: 400,
              },
            }
          )
      ),
      workingPanels: (source.geometry?.packedPanels ?? []).map((panel) => ({
        corners: [
          panel.corners[0],
          panel.corners[1],
          panel.corners[2],
          panel.corners[3],
        ],
        orientation:
          panel.orientation === 'LANDSCAPE' ? 'LANDSCAPE' : 'PORTRAIT',
        faceIndex: panel.faceIndex,
        faceId: panel.faceIndex,
        segmentIndex: panel.segmentIndex ?? -1,
        annualAcKwh: panel.annualAcKwh,
        mcsYearlyEnergyKwh: panel.mcsYearlyEnergyKwh ?? undefined,
        included: panel.onProperty !== false,
      })),
      workingObstacles: source.geometry?.obstructions ?? [],
      faceOffsets: {},
      faceLinks: [],
      excludedFaceIds: [],
      manualMode: false,
      geometryEdited: false,
    },
    placementRules,
    roofBearingRuleByFace: {},
    monthlyPolicy: null,
  };
};
